import { QuestionCircleOutlined } from "@ant-design/icons";
import {
  Checkbox,
  Col,
  Collapse,
  Form,
  InputNumber,
  Popover,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
} from "antd";
import { AktDateRangePicker } from "components/aktDatePicker";
import { ConsentCheckboxFilter } from "components/consentCheckboxFilter";
import { AktPercentageInput } from "components/aktPercentageInput";
import { IncludeExcludeInput } from "components/includeExcludeInput";
import UdfCustomFieldFilter from "features/accountsTable/components/UdfCustomFieldFilter";
import { TagSelect } from "components/tagSelect";
import { useFetchCreditorSummariesQuery } from "features/creditors/agencyPortal/creditorsAPI";
import { useFetchImportHistoryQuery } from "features/dataImport/agencyPortal/dataImportAPI";
import {
  collectorsWithRolesSelector,
  useFetchAgencyWorkflowStates,
  useFetchBackendConstantsQuery,
  useFetchCollectorsQuery,
} from "features/home/agencyPortal/homeAPI";
import { collectorFullName } from "features/home/utils";
import { useFetchProductOfferingsQuery } from "features/productOfferings/productOfferingsAPI";
import { useFetchCommunicationSequencesQuery } from "features/sequencesTable/communicationSequenceAPI";
import { useFetchCreditorTagsQuery, useFetchDebtorTagsQuery } from "features/tags/tagsAPI";
import { useFetchAllAccountUdfCustomFieldsQuery } from "features/userDefinedFields/accountUserDefinedFieldsAPI";
import { useFetchAllDebtorUdfCustomFieldsQuery } from "features/userDefinedFields/agencyPortal/debtorUserDefinedFieldsAPI";
import { useFetchAllCreditorUdfCustomFieldsQuery } from "features/userDefinedFields/agencyPortal/creditorUserDefinedFieldsAPI";
import { useFetchCommunicationLimitsQuery } from "features/communicationLimitsTable/communicationLimitsApi";
import { useFetchDesksQuery } from "features/desksTable/desksAPI";

import styled from "styled-components";
import moment from "moment-timezone";

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const StyledHeader = styled.h4`
  font-weight: 600;
  margin-top: 0px;
  margin-bottom: 8px;
`;

const StyledSpan = styled.span`
  margin-bottom: 24px;
  display: block;
  font-size: 13px;
`;

const FilterTitle = styled.div`
  font-weight: 600;
  padding: 0 0 2px;
`;

const StyledSelect = styled(Select)`
  width: 200px;
  margin-right: 4px;
`;

const StyledCollapse = styled(Collapse)`
  margin-bottom: 24px;
`;

export function AccountFiltersStep() {
  const { data: defaultWorkflowStates = [], isLoading: isDefaultWorkflowStatesLoading } =
    useFetchAgencyWorkflowStates();
  const { collectorsWithRoles: collectors = [], isLoading: isCollectorsLoading } =
    useFetchCollectorsQuery(undefined, {
      selectFromResult: (result) => ({
        ...result,
        collectorsWithRoles: collectorsWithRolesSelector(result),
      }),
    });
  const { data: creditorTags = [], isLoading: isCreditorTagsLoading } = useFetchCreditorTagsQuery();
  const { data: constants = {}, isLoading: isConstantsLoading } = useFetchBackendConstantsQuery();
  const { data: debtorTags = [], isLoading: isDebtorTagsLoading } = useFetchDebtorTagsQuery();
  const { data: creditors = [], isLoading: isCreditorsLoading } = useFetchCreditorSummariesQuery();
  const { data: productOfferings = [], isLoading: isProductOfferingsLoading } =
    useFetchProductOfferingsQuery();
  const { data: accountImports = [], isLoading: isAccountImportsLoading } =
    useFetchImportHistoryQuery({
      type: "bulk_action_account",
      statuses: ["success", "partial_success"],
    });
  const { data: debtorImports = [], isLoading: isDebtorImportsLoading } =
    useFetchImportHistoryQuery({
      type: "bulk_action_debtor",
      statuses: ["success", "partial_success"],
    });
  const { data: communicationSequences = [], isLoading: isCommunicationSequencesLoading } =
    useFetchCommunicationSequencesQuery();

  const { data: customFieldsData, isLoading: isCustomFieldsLoading } =
    useFetchAllAccountUdfCustomFieldsQuery();

  const { data: debtorCustomFieldsData, isLoading: isDebtorCustomFieldsLoading } =
    useFetchAllDebtorUdfCustomFieldsQuery();

  const { data: creditorCustomFieldsData, isLoading: isCreditorCustomFieldsLoading } =
    useFetchAllCreditorUdfCustomFieldsQuery();

  const { data: communicationLimits = [], isLoading: isCommunicationLimitsLoading } =
    useFetchCommunicationLimitsQuery();

  const { data: desks = [], isLoading: isDesksLoading } = useFetchDesksQuery();

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const items = [
    {
      key: "account",
      label: "Account Filters",
      children: (
        <>
          <IncludeExcludeInput
            label="Forwarding Entity"
            includeName={["filters", "hasForwardingEntity"]}
            excludeName={["filters", "excludeHasForwardingEntity"]}
          />

          <IncludeExcludeInput
            label="Deceased"
            includeName={["filters", "isDebtorDeceased"]}
            excludeName={["filters", "excludeIsDebtorDeceased"]}
          />

          <IncludeExcludeInput
            label="Is Settled"
            includeName={["filters", "isSettled"]}
            excludeName={["filters", "excludeIsSettled"]}
          />

          <IncludeExcludeInput
            label="Is Paid In Full"
            includeName={["filters", "isPaidInFull"]}
            excludeName={["filters", "excludeIsPaidInFull"]}
          />

          <FilterTitle>Statuses</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeStatusCodes"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select Status..."
                  filterOption={filterOption}
                  options={defaultWorkflowStates.map((state) => ({
                    label: [state.code, state.name].join(" - "),
                    value: state.code,
                    title: state.description,
                  }))}
                  disabled={isDefaultWorkflowStatesLoading}
                  loading={isDefaultWorkflowStatesLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeStatusCodes"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select Status..."
                  filterOption={filterOption}
                  options={defaultWorkflowStates.map((state) => ({
                    label: [state.code, state.name].join(" - "),
                    value: state.code,
                    title: state.description,
                  }))}
                  disabled={isDefaultWorkflowStatesLoading}
                  loading={isDefaultWorkflowStatesLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Account Custom Field</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeAccountCustomFieldFilters"]} label="Include">
                <UdfCustomFieldFilter
                  customFieldsData={customFieldsData}
                  placeholder="Add custom field filter"
                  isLoading={isCustomFieldsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeAccountCustomFieldFilters"]} label="Exclude">
                <UdfCustomFieldFilter
                  customFieldsData={customFieldsData}
                  placeholder="Add custom field filter"
                  isLoading={isCustomFieldsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Account Balance</FilterTitle>
          <Space>
            <Form.Item name={["filters", "minAccountBalance"]}>
              <InputNumber placeholder="Min" controls={false} min="0" prefix="$" step="0.01" />
            </Form.Item>
            <Form.Item name={["filters", "maxAccountBalance"]}>
              <InputNumber placeholder="Max" controls={false} min="0" prefix="$" step="0.01" />
            </Form.Item>
          </Space>

          <FilterTitle>Interest Rate</FilterTitle>
          <Space>
            <Form.Item name={["filters", "minInterestRate"]}>
              <AktPercentageInput placeholder="Min" controls={false} style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name={["filters", "maxInterestRate"]}>
              <AktPercentageInput placeholder="Max" controls={false} style={{ width: 90 }} />
            </Form.Item>
          </Space>

          <FilterTitle>Follow-up Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "followUpDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeFollowUpDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Upload Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "uploadDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeUploadDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Date of Service</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeDateOfService"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeDateOfService"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Turnover Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeTurnoverDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeTurnoverDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Open Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeOpenDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeOpenDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Date of First Delinquency</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeDateOfFirstDelinquency"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeDateOfFirstDelinquency"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Itemization Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeItemizationDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeItemizationDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Charge Off Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeChargeOffDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeChargeOffDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Interest Start Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeInterestStartDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeInterestStartDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Interest End Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeInterestEndDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeInterestEndDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Last Payment Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeLastPaymentDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeLastPaymentDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Out Of Statute Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name={["filters", "includeStatuteOfLimitationsExpiryDate"]}
                label="Include"
              >
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={["filters", "excludeStatuteOfLimitationsExpiryDate"]}
                label="Exclude"
              >
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Last Call Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeLastCallDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeLastCallDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Last Email Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeLastEmailDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeLastEmailDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Last Letter Sent Date</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeLastLetterDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeLastLetterDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>
            Payment Scheduled
            <Tooltip
              title="This filters for accounts that have a scheduled payment"
              trigger="hover"
            >
              <StyledQuestionCircleOutlined />
            </Tooltip>
          </FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeScheduledPaymentDate"]} label="Include">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeScheduledPaymentDate"]} label="Exclude">
                <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Payment Method Types</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includePaymentMethodTypes"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  maxTagTextLength={20}
                  mode="multiple"
                  placeholder="Select Payment Method Types..."
                  filterOption={filterOption}
                  options={constants.paymentMethodTypes?.map(({ value, display }) => ({
                    value,
                    label: display,
                  }))}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludePaymentMethodTypes"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  maxTagTextLength={20}
                  mode="multiple"
                  placeholder="Select Payment Method Types..."
                  filterOption={filterOption}
                  options={constants.paymentMethodTypes?.map(({ value, display }) => ({
                    value,
                    label: display,
                  }))}
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name={["filters", "collectionDebtTypes"]} label="Debt Type">
            <Checkbox.Group
              options={[
                { label: "Consumer", value: "consumer" },
                { label: "Commercial", value: "commercial" },
              ]}
            />
          </Form.Item>

          <FilterTitle>Communication Compliance Limits</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name={["filters", "compliesWithCommunicationLimitRuleIds"]}
                label="Complies with"
              >
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  allowClear
                  placeholder="Communication Compliance Limits..."
                  filterOption={filterOption}
                  options={communicationLimits.map((communicationLimitRule) => ({
                    label: communicationLimitRule.name,
                    value: communicationLimitRule.id,
                  }))}
                  disabled={isCommunicationLimitsLoading}
                  loading={isCommunicationLimitsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Assignees</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "assigneeIds"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Assignees..."
                  filterOption={filterOption}
                  options={
                    collectors && [
                      {
                        label: "Unassigned",
                        value: null,
                      },
                      ...(collectors.map((c) => ({
                        label: collectorFullName(c),
                        value: c.id,
                      })) ?? []),
                    ]
                  }
                  disabled={isCollectorsLoading}
                  loading={isCollectorsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeAssigneeIds"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Assignees..."
                  filterOption={filterOption}
                  options={
                    collectors && [
                      {
                        label: "Unassigned",
                        value: null,
                      },
                      ...collectors.map((c) => ({
                        label: collectorFullName(c),
                        value: c.id,
                      })),
                    ]
                  }
                  disabled={isCollectorsLoading}
                  loading={isCollectorsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Sales Representative</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "salesRepIds"]} label="Include">
                <Select
                  popupMatchSelectWidth={false}
                  allowClear
                  showSearch
                  filterOption={filterOption}
                  placeholder="Select a sales rep..."
                  options={collectors.map((user) => ({
                    label: collectorFullName(user),
                    value: user.id,
                  }))}
                  mode="multiple"
                  disabled={isCollectorsLoading}
                  loading={isCollectorsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeSalesRepIds"]} label="Exclude">
                <Select
                  popupMatchSelectWidth={false}
                  allowClear
                  showSearch
                  filterOption={filterOption}
                  placeholder="Select a sales rep..."
                  options={collectors.map((user) => ({
                    label: collectorFullName(user),
                    value: user.id,
                  }))}
                  mode="multiple"
                  disabled={isCollectorsLoading}
                  loading={isCollectorsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Product Offerings</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeProductOfferingIds"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Product Offering..."
                  filterOption={filterOption}
                  options={productOfferings.map((productOffering) => ({
                    label: productOffering.name,
                    value: productOffering.id,
                  }))}
                  disabled={isProductOfferingsLoading}
                  loading={isProductOfferingsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeProductOfferingIds"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Product Offering..."
                  filterOption={filterOption}
                  options={productOfferings.map((productOffering) => ({
                    label: productOffering.name,
                    value: productOffering.id,
                  }))}
                  disabled={isProductOfferingsLoading}
                  loading={isProductOfferingsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Account Imports</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeAccountDataImportIds"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  allowClear
                  placeholder="Account Imports..."
                  filterOption={filterOption}
                  options={accountImports.map((accountImport) => ({
                    label: `${accountImport.filename} - ${moment(accountImport.createdAt).format(
                      "MM/DD/YY HH:mm",
                    )}`,
                    value: accountImport.id,
                  }))}
                  disabled={isAccountImportsLoading}
                  loading={isAccountImportsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeAccountDataImportIds"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  allowClear
                  placeholder="Account Imports..."
                  filterOption={filterOption}
                  options={accountImports.map((accountImport) => ({
                    label: `${accountImport.filename} - ${moment(accountImport.createdAt).format(
                      "MM/DD/YY HH:mm",
                    )}`,
                    value: accountImport.id,
                  }))}
                  disabled={isAccountImportsLoading}
                  loading={isAccountImportsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Communication Sequences</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeCommunicationSequenceIds"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  allowClear
                  placeholder="Communication Sequences ..."
                  filterOption={filterOption}
                  options={
                    communicationSequences && [
                      {
                        label: "Unassigned",
                        value: null,
                      },
                      ...communicationSequences.map((communicationSequence) => ({
                        label: communicationSequence.name,
                        value: communicationSequence.id,
                      })),
                    ]
                  }
                  disabled={isCommunicationSequencesLoading}
                  loading={isCommunicationSequencesLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeCommunicationSequenceIds"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  allowClear
                  placeholder="Communication Sequences ..."
                  filterOption={filterOption}
                  options={
                    communicationSequences && [
                      {
                        label: "Unassigned",
                        value: null,
                      },
                      ...communicationSequences.map((communicationSequence) => ({
                        label: communicationSequence.name,
                        value: communicationSequence.id,
                      })),
                    ]
                  }
                  disabled={isCommunicationSequencesLoading}
                  loading={isCommunicationSequencesLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Desks</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeDeskIds"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  allowClear
                  placeholder="Desks ..."
                  filterOption={filterOption}
                  options={
                    desks && [
                      {
                        label: "Unassigned",
                        value: null,
                      },
                      ...desks.map((desk) => ({
                        label: desk.name,
                        value: desk.id,
                      })),
                    ]
                  }
                  disabled={isDesksLoading}
                  loading={isDesksLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeDeskIds"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  allowClear
                  placeholder="Desks ..."
                  filterOption={filterOption}
                  options={
                    desks && [
                      {
                        label: "Unassigned",
                        value: null,
                      },
                      ...desks.map((desk) => ({
                        label: desk.name,
                        value: desk.id,
                      })),
                    ]
                  }
                  disabled={isDesksLoading}
                  loading={isDesksLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Score</FilterTitle>
          <Space>
            <Form.Item name={["filters", "minExternalScore"]}>
              <InputNumber placeholder="Min" controls={false} min="0" step="0.01" />
            </Form.Item>
            <Form.Item name={["filters", "maxExternalScore"]}>
              <InputNumber placeholder="Max" controls={false} min="0" step="0.01" />
            </Form.Item>
          </Space>

          <FilterTitle>Intelitech I-Score</FilterTitle>
          <Space>
            <Form.Item name={["filters", "minIntelitechIScore"]}>
              <InputNumber placeholder="Min" controls={false} min="0" step="1" max="999" />
            </Form.Item>
            <Form.Item name={["filters", "maxIntelitechIScore"]}>
              <InputNumber placeholder="Max" controls={false} min="0" step="1" max="999" />
            </Form.Item>
          </Space>

          <FilterTitle>Intelitech Medal-Score</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeIntelitechMedalScore"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Medal Scores..."
                  filterOption={filterOption}
                  options={constants?.intelitechMedalScores ?? []}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeIntelitechMedalScore"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Medal Scores..."
                  filterOption={filterOption}
                  options={constants?.intelitechMedalScores ?? []}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Intelitech Q-Score</FilterTitle>
          <Space>
            <Form.Item name={["filters", "minIntelitechQScore"]}>
              <InputNumber placeholder="Min" controls={false} min="0" step="0.01" />
            </Form.Item>
            <Form.Item name={["filters", "maxIntelitechQScore"]}>
              <InputNumber placeholder="Max" controls={false} min="0" step="0.01" />
            </Form.Item>
          </Space>

          <FilterTitle>Intelitech R-Score</FilterTitle>
          <Space>
            <Form.Item name={["filters", "minIntelitechRScore"]}>
              <InputNumber placeholder="Min" controls={false} min="0" step="0.01" />
            </Form.Item>
            <Form.Item name={["filters", "maxIntelitechRScore"]}>
              <InputNumber placeholder="Max" controls={false} min="0" step="0.01" />
            </Form.Item>
          </Space>

          <FilterTitle>Intelitech FPG-Value</FilterTitle>
          <Space>
            <Form.Item name={["filters", "minIntelitechFpgValue"]}>
              <InputNumber placeholder="Min" controls={false} min="0" step="0.01" />
            </Form.Item>
            <Form.Item name={["filters", "maxIntelitechFpgValue"]}>
              <InputNumber placeholder="Max" controls={false} min="0" step="0.01" />
            </Form.Item>
          </Space>
        </>
      ),
    },
    {
      key: "legal",
      label: "Legal Filters",
      children: (
        <>
          <IncludeExcludeInput
            label="Lawsuit Filed"
            includeName={["filters", "hasLawsuit"]}
            excludeName={["filters", "excludeHasLawsuit"]}
          />
          <IncludeExcludeInput
            label="Has Judgment"
            includeName={["filters", "includeHasJudgment"]}
            excludeName={["filters", "excludeHasJudgment"]}
          />
        </>
      ),
    },
    {
      key: "creditReporting",
      label: "Credit Reporting Filters",
      children: (
        <>
          <IncludeExcludeInput
            label="Surpress From Credit Reporting"
            includeName={["filters", "includeSuppressFromCreditReporting"]}
            excludeName={["filters", "excludeSuppressFromCreditReporting"]}
          />
          <IncludeExcludeInput
            label="Delete From Credit Reporting"
            includeName={["filters", "includeDeleteFromCreditReporting"]}
            excludeName={["filters", "excludeDeleteFromCreditReporting"]}
          />
          <FilterTitle>CBR Class Code</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeCbrClassCodes"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select CBR Class Code..."
                  filterOption={filterOption}
                  options={(constants.cbrClassCodes ?? []).map((cbrClassCode) => ({
                    label: cbrClassCode.display,
                    value: cbrClassCode.value,
                  }))}
                  disabled={isDefaultWorkflowStatesLoading}
                  loading={isDefaultWorkflowStatesLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeCbrClassCodes"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select CBR Class Code..."
                  filterOption={filterOption}
                  options={(constants.cbrClassCodes ?? []).map((cbrClassCode) => ({
                    label: cbrClassCode.display,
                    value: cbrClassCode.value,
                  }))}
                  disabled={isDefaultWorkflowStatesLoading}
                  loading={isDefaultWorkflowStatesLoading}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      ),
    },
    {
      key: "debtor",
      label: "Debtor Filters",
      children: (
        <>
          <FilterTitle>
            Debtor Location
            <Popover
              content="This filters by the primary address of the debtor"
              title=""
              trigger="hover"
              placement="right"
            >
              <StyledQuestionCircleOutlined />
            </Popover>
          </FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "debtorStates"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select location..."
                  filterOption={filterOption}
                  options={[
                    {
                      label: "US States",
                      options:
                        constants?.states?.map((state) => ({
                          label: `${state.display} (${state.value})`,
                          value: state.value,
                        })) ?? [],
                    },
                    {
                      label: "Canadian Provinces",
                      options:
                        constants?.canadianProvinces?.map((province) => ({
                          label: `${province.display} (${province.value})`,
                          value: province.value,
                        })) ?? [],
                    },
                  ]}
                  disabled={isConstantsLoading}
                  loading={isConstantsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeDebtorStates"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select location..."
                  filterOption={filterOption}
                  options={[
                    {
                      label: "US States",
                      options:
                        constants?.states?.map((state) => ({
                          label: `${state.display} (${state.value})`,
                          value: state.value,
                        })) ?? [],
                    },
                    {
                      label: "Canadian Provinces",
                      options:
                        constants?.canadianProvinces?.map((province) => ({
                          label: `${province.display} (${province.value})`,
                          value: province.value,
                        })) ?? [],
                    },
                  ]}
                  disabled={isConstantsLoading}
                  loading={isConstantsLoading}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={12}>
              Override operating states
              <Popover
                content="If enabled, the bulk action can apply to accounts outside the operating state"
                title=""
                trigger="hover"
                placement="right"
              >
                <StyledQuestionCircleOutlined />
              </Popover>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "ignoreOperatingStates"]}>
                <Switch checkedChildren="Enable" unCheckedChildren="Disable" />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Debtor Tags</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "debtorTagIds"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select Debtor Tags..."
                  filterOption={filterOption}
                  options={debtorTags.map((tag) => ({
                    value: tag.id,
                    label: tag.name,
                  }))}
                  disabled={isDebtorTagsLoading}
                  loading={isDebtorTagsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeDebtorTagIds"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select Debtor Tags..."
                  filterOption={filterOption}
                  options={debtorTags.map((tag) => ({
                    value: tag.id,
                    label: tag.name,
                  }))}
                  disabled={isDebtorTagsLoading}
                  loading={isDebtorTagsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Debtor Custom Field</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeDebtorCustomFieldFilters"]} label="Include">
                <UdfCustomFieldFilter
                  customFieldsData={debtorCustomFieldsData}
                  placeholder="Add custom field filter"
                  isLoading={isDebtorCustomFieldsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeDebtorCustomFieldFilters"]} label="Exclude">
                <UdfCustomFieldFilter
                  customFieldsData={debtorCustomFieldsData}
                  placeholder="Add custom field filter"
                  isLoading={isDebtorCustomFieldsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Debtors Imports</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "debtorDataImportIds"]} label="Include">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select Debtors Imports..."
                  filterOption={filterOption}
                  options={debtorImports.map((debtorImport) => ({
                    value: debtorImport.id,
                    label: `${debtorImport.filename} - ${moment(debtorImport.createdAt).format(
                      "MM/DD/YY HH:mm",
                    )}`,
                  }))}
                  disabled={isDebtorImportsLoading}
                  loading={isDebtorImportsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeDebtorDataImportIds"]} label="Exclude">
                <StyledSelect
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select Debtors Imports..."
                  filterOption={filterOption}
                  options={debtorImports.map((debtorImport) => ({
                    value: debtorImport.id,
                    label: `${debtorImport.filename} - ${moment(debtorImport.createdAt).format(
                      "MM/DD/YY HH:mm",
                    )}`,
                  }))}
                  disabled={isDebtorImportsLoading}
                  loading={isDebtorImportsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <IncludeExcludeInput
            label="Has Employment"
            includeName={["filters", "debtorHasEmployment"]}
            excludeName={["filters", "excludeDebtorHasEmployment"]}
          />
          <ConsentCheckboxFilter
            label="Email Consent"
            name={["filters", "debtorEmailConsentStatuses"]}
          />
          <ConsentCheckboxFilter
            label="Text Consent"
            name={["filters", "debtorTextConsentStatuses"]}
          />
          <ConsentCheckboxFilter
            label="AI Calls Consent"
            name={["filters", "debtorAiCallsConsentStatuses"]}
          />
        </>
      ),
    },
    {
      key: "client",
      label: "Client Filters",
      children: (
        <>
          <FilterTitle>Clients</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "creditorIds"]} label="Include">
                <StyledSelect
                  maxTagTextLength={20}
                  mode="multiple"
                  placeholder="Select Clients..."
                  filterOption={filterOption}
                  options={creditors.map((creditor) => ({
                    value: creditor.id,
                    label: creditor.code ? `${creditor.name} (${creditor.code})` : creditor.name,
                  }))}
                  disabled={isCreditorsLoading}
                  loading={isCreditorsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeCreditorIds"]} label="Exclude">
                <StyledSelect
                  maxTagTextLength={20}
                  mode="multiple"
                  placeholder="Select Clients..."
                  filterOption={filterOption}
                  options={creditors.map((creditor) => ({
                    value: creditor.id,
                    label: creditor.code ? `${creditor.name} (${creditor.code})` : creditor.name,
                  }))}
                  disabled={isCreditorsLoading}
                  loading={isCreditorsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Client Tags</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "creditorTagIds"]} label="Include">
                <StyledSelect
                  showSearch
                  maxTagTextLength={20}
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select Client Tags..."
                  filterOption={filterOption}
                  options={creditorTags.map((creditorTag) => ({
                    value: creditorTag.id,
                    label: creditorTag.name,
                  }))}
                  disabled={isCreditorTagsLoading}
                  loading={isCreditorTagsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeCreditorTagIds"]} label="Exclude">
                <StyledSelect
                  maxTagTextLength={20}
                  popupMatchSelectWidth={false}
                  mode="multiple"
                  placeholder="Select Client Tags..."
                  filterOption={filterOption}
                  options={creditorTags.map((creditorTag) => ({
                    value: creditorTag.id,
                    label: creditorTag.name,
                  }))}
                  disabled={isCreditorTagsLoading}
                  loading={isCreditorTagsLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <FilterTitle>Client Codes</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "creditorCodes"]} label="Include">
                <StyledSelect
                  maxTagTextLength={20}
                  mode="multiple"
                  placeholder="Select Codes..."
                  filterOption={filterOption}
                  options={creditors
                    .filter((creditor) => !!creditor.code)
                    .map((creditor) => ({
                      value: creditor.code,
                      label: creditor.code ? `${creditor.name} (${creditor.code})` : creditor.name,
                    }))}
                  disabled={isCreditorsLoading}
                  loading={isCreditorsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeCreditorCodes"]} label="Exclude">
                <StyledSelect
                  maxTagTextLength={20}
                  mode="multiple"
                  placeholder="Select Codes..."
                  filterOption={filterOption}
                  options={creditors
                    .filter((creditor) => !!creditor.code)
                    .map((creditor) => ({
                      value: creditor.code,
                      label: creditor.code ? `${creditor.name} (${creditor.code})` : creditor.name,
                    }))}
                  disabled={isCreditorsLoading}
                  loading={isCreditorsLoading}
                />
              </Form.Item>
            </Col>
          </Row>
          <FilterTitle>Client Custom Field</FilterTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["filters", "includeCreditorCustomFieldFilters"]} label="Include">
                <UdfCustomFieldFilter
                  customFieldsData={creditorCustomFieldsData}
                  placeholder="Add custom field filter"
                  isLoading={isCreditorCustomFieldsLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["filters", "excludeCreditorCustomFieldFilters"]} label="Exclude">
                <UdfCustomFieldFilter
                  customFieldsData={creditorCustomFieldsData}
                  placeholder="Add custom field filter"
                  isLoading={isCreditorCustomFieldsLoading}
                />
              </Form.Item>
            </Col>
          </Row>
          <FilterTitle>Original Creditor</FilterTitle>
          <Col span={12}>
            <Form.Item name={["filters", "originalCreditorNames"]}>
              <TagSelect placeholder="Name (case insensitive)" />
            </Form.Item>
          </Col>
        </>
      ),
    },
  ];

  return (
    <>
      <StyledHeader>Filters</StyledHeader>
      <StyledSpan>Accounts matching these filter criteria will be selected.</StyledSpan>

      <Form.Item hidden name="type" />

      <StyledCollapse accordion defaultActiveKey={["account"]}>
        {items.map((item) => (
          <Collapse.Panel header={item.label} key={item.key} forceRender>
            {item.children}
          </Collapse.Panel>
        ))}
      </StyledCollapse>
    </>
  );
}
